import { useState } from 'react';

import type { PermissionTreeGroup, RoleOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Spinner, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 계정 관리 > Role — 레거시 WebService/auth/role/{list,detail,add,delete} 대응.
 *
 * 목록은 서버가 `depth >= 열람자 depth` 로 걸러 내려주므로 상위 역할은 보이지 않는다.
 * 상세는 이름/depth + 레거시 getPermissionMenu 격자(19 그룹)의 2단 체크박스로
 * 권한 집합을 편집한다. 저장은 전체 교체(PUT)라 체크를 빼면 그 권한이 빠진다.
 * 시드 역할(1~9)과 사용자가 붙은 역할은 서버가 삭제를 409 로 거부한다 —
 * 메시지를 그대로 보여준다.
 */
export function RoleManage() {
  const roles = useAsync(() => api.listRoles(), []);
  const tree = useAsync(() => api.permissionTree(), []);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const row = roles.data?.find((r) => r.id === selected) ?? null;

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
            역할 등록
          </button>
          <span className="muted">사용자 정의 역할은 10번부터 번호가 붙습니다.</span>
        </div>

        {roles.loading && <Spinner />}
        {roles.error && <ErrorBox message={roles.error} onRetry={roles.reload} />}

        {roles.data && (
          <table className="grid">
            <thead>
              <tr>
                <th className="num">ID</th>
                <th>이름</th>
                <th className="num">depth</th>
                <th className="num">권한 수</th>
                <th className="num">사용자 수</th>
              </tr>
            </thead>
            <tbody>
              {roles.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    조회 가능한 역할이 없습니다.
                  </td>
                </tr>
              )}
              {roles.data.map((r) => (
                <tr
                  key={r.id}
                  className={r.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(r.id)}
                >
                  <td className="num">{r.id}</td>
                  <td>
                    {r.name} {r.seeded && <span className="badge">기본</span>}
                  </td>
                  <td className="num">{r.depth}</td>
                  <td className="num">{r.permission_codes.length}</td>
                  <td className="num">{r.user_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <aside className="admin__aside">
        {tree.error && <ErrorBox message={tree.error} onRetry={tree.reload} />}
        {row === null || !tree.data ? (
          <p className="muted">역할을 선택하세요.</p>
        ) : (
          <RoleEdit
            key={row.id}
            row={row}
            tree={tree.data}
            onClose={() => setSelected(null)}
            onChanged={() => roles.reload()}
            onDeleted={() => {
              setSelected(null);
              roles.reload();
            }}
          />
        )}
      </aside>

      {createOpen && tree.data && (
        <RoleCreateModal
          tree={tree.data}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => {
            setCreateOpen(false);
            setSelected(id);
            roles.reload();
          }}
        />
      )}
    </div>
  );
}

/** 체크 집합 편집 훅 — 상세/등록 공용. */
function useChecked(initial: string[]) {
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set(initial));
  const toggle = (code: string) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const setMany = (codes: string[], on: boolean) =>
    setChecked((prev) => {
      const next = new Set(prev);
      for (const c of codes) {
        if (on) next.add(c);
        else next.delete(c);
      }
      return next;
    });
  return { checked, toggle, setMany };
}

/**
 * 2단 체크박스 격자 — 레거시 role/detail.blade.php 의 그룹(부모) 체크 → 하위 전체
 * 선택/해제 동작을 그대로 옮겼다. 그룹 체크박스는 일부만 선택되면 indeterminate.
 */
function PermissionGrid({
  tree,
  checked,
  onToggle,
  onGroup,
}: {
  tree: PermissionTreeGroup[];
  checked: ReadonlySet<string>;
  onToggle: (code: string) => void;
  onGroup: (codes: string[], on: boolean) => void;
}) {
  return (
    <div className="engineer-picker">
      {tree.map((g) => {
        const codes = g.items.map((i) => i.code);
        const on = codes.filter((c) => checked.has(c)).length;
        const all = on === codes.length;
        return (
          <div key={g.key} className="panel" style={{ marginBottom: 8 }}>
            <label className="check">
              <input
                type="checkbox"
                checked={all}
                ref={(el) => {
                  if (el) el.indeterminate = on > 0 && !all;
                }}
                onChange={() => onGroup(codes, !all)}
              />
              <strong>{g.label}</strong>
              <span className="muted">
                ({on}/{codes.length})
              </span>
            </label>
            <div className="row" style={{ flexWrap: 'wrap', paddingLeft: 24 }}>
              {g.items.map((item) => (
                <label key={item.code} className="check" title={item.code}>
                  <input
                    type="checkbox"
                    checked={checked.has(item.code)}
                    onChange={() => onToggle(item.code)}
                  />
                  {item.label}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RoleEdit({
  row,
  tree,
  onClose,
  onChanged,
  onDeleted,
}: {
  row: RoleOut;
  tree: PermissionTreeGroup[];
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(row.name);
  const [depth, setDepth] = useState(String(row.depth));
  const { checked, toggle, setMany } = useChecked(row.permission_codes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    const d = Number(depth);
    if (!name.trim()) {
      setError('역할 이름을 입력해 주세요.');
      return;
    }
    if (!Number.isInteger(d) || d < 1) {
      setError('depth 는 1 이상의 정수여야 합니다.');
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.saveRole(row.id, { name: name.trim(), depth: d, permission_codes: [...checked] });
      setOk('저장했습니다. 해당 역할 사용자는 다음 요청부터 새 권한이 적용됩니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteRole(row.id);
      onDeleted();
    } catch (err) {
      // 시드 역할·사용자가 붙은 역할의 409 메시지를 그대로 보여준다.
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  const deletable = !row.seeded && row.user_count === 0;

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>
          #{row.id} {row.name}
        </h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel form-grid">
        <label className="f">
          역할 이름 *
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="f">
          depth (작을수록 상위)
          <input
            type="number"
            min={1}
            value={depth}
            onChange={(e) => setDepth(e.target.value)}
          />
        </label>
        <p className="muted wide">
          사용자 {row.user_count}명 · 권한 {checked.size}개
          {row.seeded && ' · 기본 역할(1~9)은 삭제할 수 없습니다.'}
        </p>
      </section>

      <h3 className="section">권한 ({checked.size}개)</h3>
      <PermissionGrid tree={tree} checked={checked} onToggle={toggle} onGroup={setMany} />

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}

      <div className="row form-actions">
        <button
          type="button"
          disabled={busy || !deletable}
          title={deletable ? undefined : '기본 역할이거나 사용자가 있어 삭제할 수 없습니다.'}
          onClick={() => setConfirmDelete(true)}
        >
          삭제
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmDelete && (
        <Modal title="역할 삭제" onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{row.name}</strong> 역할을 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RoleCreateModal({
  tree,
  onClose,
  onCreated,
}: {
  tree: PermissionTreeGroup[];
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [name, setName] = useState('');
  // 새 역할은 최하위(depth 5)부터 — 상위로 올리는 건 의도적으로 한 번 더 손이 가게 둔다.
  const [depth, setDepth] = useState('5');
  const { checked, toggle, setMany } = useChecked([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const d = Number(depth);
    if (!name.trim()) {
      setError('역할 이름을 입력해 주세요.');
      return;
    }
    if (!Number.isInteger(d) || d < 1) {
      setError('depth 는 1 이상의 정수여야 합니다.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const created = await api.saveRole(null, {
        name: name.trim(),
        depth: d,
        permission_codes: [...checked],
      });
      onCreated(created.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="역할 등록" onClose={onClose}>
      <label className="labeled">
        역할 이름 *
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="labeled">
        depth (작을수록 상위)
        <input type="number" min={1} value={depth} onChange={(e) => setDepth(e.target.value)} />
      </label>
      <PermissionGrid tree={tree} checked={checked} onToggle={toggle} onGroup={setMany} />

      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
